import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { logActivity } from '../../../services/teamService';

const ProfileTab = () => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [profileForm, setProfileForm] = useState({
    fullName: user?.user_metadata?.full_name || '',
    phone: user?.user_metadata?.phone || '',
    jobTitle: user?.user_metadata?.job_title || '',
    department: user?.user_metadata?.department || '',
    avatarUrl: user?.user_metadata?.avatar_url || ''
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');
  const [avatarUploading, setAvatarUploading] = useState(false);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const handleAvatarUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes?.includes(file?.type)) {
      setProfileError('Only JPEG, PNG, and WebP images are allowed.');
      return;
    }
    if (file?.size > 5 * 1024 * 1024) {
      setProfileError('Image must be under 5MB.');
      return;
    }

    setAvatarUploading(true);
    setProfileError('');

    // Safety timeout: reset uploading state after 30s to prevent infinite spinner
    const uploadTimeout = setTimeout(() => {
      setAvatarUploading(false);
      setProfileError('Upload timed out. Please try again.');
    }, 30000);

    try {
      const ext = file?.name?.split('.')?.pop();
      const filePath = `avatars/${user?.id}-${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase?.storage?.from('project-photos')?.upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase?.storage?.from('project-photos')?.getPublicUrl(filePath);

      setProfileForm(prev => ({ ...prev, avatarUrl: publicUrl }));
    } catch (err) {
      console.error('Avatar upload error:', err);
      setProfileError('Failed to upload avatar. Please try again.');
    } finally {
      clearTimeout(uploadTimeout);
      setAvatarUploading(false);
    }
  };

  const handleProfileSave = async (e) => {
    e?.preventDefault();
    setProfileLoading(true);
    setProfileError('');
    setProfileSuccess('');
    try {
      // Update user_profiles table only.
      // NOTE: We intentionally do NOT call supabase.auth.updateUser() here because
      // it fires an onAuthStateChange USER_UPDATED event which re-runs fetchUserOrganization.
      // If that re-fetch fails (e.g. RLS timing), it clears the in-memory org_id and
      // breaks all subsequent data fetches across the app.
      const { error: profileUpdateError } = await supabase?.from('user_profiles')?.update({
          full_name: profileForm?.fullName,
          phone: profileForm?.phone,
          job_title: profileForm?.jobTitle,
          department: profileForm?.department,
          avatar_url: profileForm?.avatarUrl,
          updated_at: new Date()?.toISOString()
        })?.eq('id', user?.id);

      if (profileUpdateError) throw profileUpdateError;

      await logActivity('update', 'user_profile', user?.id, { updated_fields: ['full_name', 'phone', 'job_title', 'department', 'avatar_url'] });
      setProfileSuccess('Profile updated successfully.');
      setTimeout(() => setProfileSuccess(''), 4000);
    } catch (err) {
      console.error('Profile save error:', err);
      setProfileError('Failed to update profile. Please try again.');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e?.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (passwordForm?.newPassword?.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }
    if (passwordForm?.newPassword !== passwordForm?.confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }

    setPasswordLoading(true);
    try {
      const { error } = await supabase?.auth?.updateUser({
        password: passwordForm?.newPassword
      });

      if (error) throw error;

      await logActivity('update', 'user_profile', user?.id, { action: 'password_changed' });
      setPasswordSuccess('Password updated successfully.');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setPasswordSuccess(''), 4000);
    } catch (err) {
      console.error('Password change error:', err);
      setPasswordError(err?.message || 'Failed to update password. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const toggleShowPassword = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev?.[field] }));
  };

  return (
    <div className="space-y-6">
      {/* Profile Information */}
      <div className="bg-card rounded-xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-foreground mb-2">Profile Information</h3>
        <p className="text-sm text-muted-foreground mb-6">Update your personal details and profile picture</p>

        <form onSubmit={handleProfileSave} className="space-y-6">
          {/* Avatar */}
          <div className="flex items-center gap-6">
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden border-2 border-border">
                {profileForm?.avatarUrl ? (
                  <img src={profileForm?.avatarUrl} alt="Profile avatar" className="w-full h-full object-cover" />
                ) : (
                  <Icon name="User" size={36} className="text-primary" />
                )}
              </div>
              {avatarUploading && (
                <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                </div>
              )}
            </div>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleAvatarUpload}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef?.current?.click()}
                disabled={avatarUploading}
              >
                <Icon name="Upload" size={16} />
                <span>{avatarUploading ? 'Uploading...' : 'Upload Photo'}</span>
              </Button>
              <p className="text-xs text-muted-foreground mt-1">JPEG, PNG, WebP up to 5MB</p>
            </div>
          </div>

          {/* Name & Email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Full Name</label>
              <Input
                type="text"
                value={profileForm?.fullName}
                onChange={(e) => setProfileForm(prev => ({ ...prev, fullName: e?.target?.value }))}
                placeholder="Your full name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Email Address</label>
              <Input
                type="email"
                value={user?.email || ''}
                disabled
                className="opacity-60 cursor-not-allowed"
              />
              <p className="text-xs text-muted-foreground mt-1">Email cannot be changed here</p>
            </div>
          </div>

          {/* Phone & Job Title */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Phone Number</label>
              <Input
                type="tel"
                value={profileForm?.phone}
                onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e?.target?.value }))}
                placeholder="(555) 123-4567"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Job Title</label>
              <Input
                type="text"
                value={profileForm?.jobTitle}
                onChange={(e) => setProfileForm(prev => ({ ...prev, jobTitle: e?.target?.value }))}
                placeholder="e.g. Project Manager"
              />
            </div>
          </div>

          {/* Department */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Department</label>
            <Input
              type="text"
              value={profileForm?.department}
              onChange={(e) => setProfileForm(prev => ({ ...prev, department: e?.target?.value }))}
              placeholder="e.g. Operations"
            />
          </div>

          {/* Feedback */}
          {profileError && (
            <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg text-sm text-error">
              <Icon name="AlertCircle" size={16} />
              <span>{profileError}</span>
            </div>
          )}
          {profileSuccess && (
            <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/20 rounded-lg text-sm text-success">
              <Icon name="CheckCircle" size={16} />
              <span>{profileSuccess}</span>
            </div>
          )}

          <div className="flex justify-end">
            <Button type="submit" disabled={profileLoading}>
              {profileLoading ? (
                <><Icon name="Loader2" size={18} className="animate-spin" /><span>Saving...</span></>
              ) : (
                <><Icon name="Save" size={18} /><span>Save Profile</span></>
              )}
            </Button>
          </div>
        </form>
      </div>
      {/* Security / Password Change */}
      <div className="bg-card rounded-xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-foreground mb-2">Change Password</h3>
        <p className="text-sm text-muted-foreground mb-6">Update your account password</p>

        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">New Password</label>
            <div className="relative">
              <Input
                type={showPasswords?.new ? 'text' : 'password'}
                value={passwordForm?.newPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e?.target?.value }))}
                placeholder="At least 8 characters"
                required
              />
              <button
                type="button"
                onClick={() => toggleShowPassword('new')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <Icon name={showPasswords?.new ? 'EyeOff' : 'Eye'} size={16} />
              </button>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Confirm New Password</label>
            <div className="relative">
              <Input
                type={showPasswords?.confirm ? 'text' : 'password'}
                value={passwordForm?.confirmPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e?.target?.value }))}
                placeholder="Repeat new password"
                required
              />
              <button
                type="button"
                onClick={() => toggleShowPassword('confirm')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <Icon name={showPasswords?.confirm ? 'EyeOff' : 'Eye'} size={16} />
              </button>
            </div>
          </div>

          {/* Password strength hint */}
          {passwordForm?.newPassword && (
            <div className="space-y-1">
              <div className="flex gap-1">
                {[1,2,3,4]?.map(i => (
                  <div
                    key={i}
                    className={`h-1 flex-1 rounded-full transition-colors ${
                      passwordForm?.newPassword?.length >= i * 3
                        ? passwordForm?.newPassword?.length >= 12 ? 'bg-success'
                          : passwordForm?.newPassword?.length >= 8 ? 'bg-warning' :'bg-error' :'bg-muted'
                    }`}
                  />
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {passwordForm?.newPassword?.length < 8 ? 'Too short' :
                  passwordForm?.newPassword?.length < 12 ? 'Fair — consider a longer password': 'Strong password'}
              </p>
            </div>
          )}

          {/* Feedback */}
          {passwordError && (
            <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg text-sm text-error">
              <Icon name="AlertCircle" size={16} />
              <span>{passwordError}</span>
            </div>
          )}
          {passwordSuccess && (
            <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/20 rounded-lg text-sm text-success">
              <Icon name="CheckCircle" size={16} />
              <span>{passwordSuccess}</span>
            </div>
          )}

          <div className="flex justify-end">
            <Button type="submit" disabled={passwordLoading}>
              {passwordLoading ? (
                <><Icon name="Loader2" size={18} className="animate-spin" /><span>Updating...</span></>
              ) : (
                <><Icon name="Lock" size={18} /><span>Update Password</span></>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileTab;
